import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../services/api';
import useAuthStore from '../store/useAuthStore';

const demoAccounts = [
  { email: 'admin@dektal360.local', clinic: 'Legacy Clinic' },
  { email: 'admin@dektal360.com', clinic: 'Dektal 360 Admin Clinic' },
  { email: 'priya@dektal360.com', clinic: 'Smile Craft Dental' },
  { email: 'rahul@dektal360.com', clinic: 'Bright Tooth Clinic' },
  { email: 'neha@dektal360.com', clinic: 'Care32 Dental Studio' },
  { email: 'arjun@dektal360.com', clinic: 'Healthy Smile Hub' },
  { email: 'sneha@dektal360.com', clinic: 'Pearl White Dental' },
];

const showDemoAccounts = process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === 'true';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get('/auth/google/config')
      .then(({ data }) => {
        if (active) setGoogleConfigured(Boolean(data.configured));
      })
      .catch(() => {
        if (active) setGoogleConfigured(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const blockedReason = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const reason = queryParams.get('reason');

    switch (reason) {
      case 'SUBSCRIPTION_EXPIRED':
        return 'Your subscription has expired. Renew it to continue accessing your clinic workspace.';
      case 'SUBSCRIPTION_PAST_DUE':
        return 'Your subscription payment is overdue. Renew it to continue accessing your clinic workspace.';
      case 'SUBSCRIPTION_CANCELLED':
        return 'Your subscription is cancelled. Renew it to continue accessing your clinic workspace.';
      case 'SUBSCRIPTION_INACTIVE':
        return 'Your subscription is inactive. Renew it to continue accessing your clinic workspace.';
      case 'SUBSCRIPTION_PENDING':
        return 'Your clinic subscription is pending activation. Complete payment or setup to continue.';
      case 'SUBSCRIPTION_REQUIRED':
        return 'No active subscription record is available for this clinic yet. Please complete signup or contact support.';
      default:
        return '';
    }
  }, [location.search]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fillDemoAccount = (email) => {
    setForm({ email, password: 'admin123' });
    setError('');
  };

  const redirectAfterLogin = (user) => {
    if (user?.role === 'platform_admin') {
      navigate('/platform-admin/business-control', { replace: true });
      return;
    }
    if (user?.subscriptionAccess && !user.subscriptionAccess.allowed) {
      navigate('/subscription/renew', { replace: true });
      return;
    }
    navigate(user?.setupCompleted ? '/dashboard' : '/setup', { replace: true });
  };

  const startGoogleLogin = () => {
    if (!googleConfigured) {
      setError('Google login is not configured for this environment.');
      return;
    }
    window.location.href = `${API_BASE_URL}/auth/google/start?flow=login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/auth/login', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      login(data.token, data.user);
      redirectAfterLogin(data.user);
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err?.request) {
        setError(`Login failed because the backend did not respond at ${API_BASE_URL}. Please start the backend and check REACT_APP_API_URL.`);
      } else {
        setError(err?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-8 text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Clinic OS</div>
            <h1 className="mt-3 text-3xl font-bold text-slate-900">Dektal 360 Pro</h1>
            <p className="mt-2 text-sm text-slate-500">Sign in to access your clinic workspace.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <button
              type="button"
              onClick={startGoogleLogin}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-semibold text-slate-800 hover:border-indigo-400"
            >
              Continue with Google
            </button>

            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              Email login
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                placeholder="doctor@clinic.com"
                required
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                placeholder="Enter password"
                required
              />
            </div>

            {error ? (
              <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</div>
            ) : null}

            {!error && blockedReason ? (
              <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
                {blockedReason}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-white font-semibold disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
            New clinic?{' '}
            <Link to="/subscribe" className="font-semibold text-indigo-600 hover:text-indigo-700">
              Subscribe first
            </Link>
          </div>
        </div>

        {showDemoAccounts ? (
          <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
            <h2 className="text-2xl font-bold text-slate-900">Demo logins for tenant testing</h2>
            <p className="mt-2 text-sm text-slate-500">
              Each email below belongs to a separate tenant, so clinic setup and business data stay isolated.
            </p>

            <div className="mt-6 space-y-4">
              {demoAccounts.map((account) => (
                <button
                  key={account.email}
                  type="button"
                  onClick={() => fillDemoAccount(account.email)}
                  className="w-full text-left rounded-2xl border border-slate-200 p-4 hover:border-indigo-400 hover:bg-indigo-50 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="mobile-safe-text text-base font-semibold text-slate-900 sm:text-lg">{account.email}</div>
                      <div className="mt-1 text-sm text-slate-500">Clinic: {account.clinic}</div>
                    </div>
                    <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      Password: admin123
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Existing demo/legacy users go directly to the app. Newly registered users will be sent to Setup first.
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-between rounded-3xl bg-white p-6 shadow-xl sm:p-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Multi-clinic secure access</h2>
              <p className="mt-2 text-sm text-slate-500">
                Every clinic signs into an isolated tenant workspace where data never intersects across clinics.
              </p>
            </div>
            <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
              New clinic? Use <Link to="/subscribe" className="font-semibold underline">Subscribe first</Link> to start onboarding.
              <div className="mt-3 text-indigo-900">
                Payments are verified through Razorpay and no clinic data is shared across tenants.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
